import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchCommonCodes } from '@/shared/apis/common-codes';

interface UseCommonCodesProps {
  optionParams?: string;
  // Optional and defaulted to the previous behaviour. Exists for groups whose
  // upper code is only known after another selection is made - city/regency
  // hangs off the chosen province - so nothing is fetched until it is.
  enabled?: boolean;
}

const useCommonCodes = ({ optionParams = '', enabled = true }: UseCommonCodesProps) => {
  // The backend resolves code names from the session user's stored
  // language_preference, so the cache must be keyed by language too - without
  // this, a header EN/ID toggle left every common-code dropdown showing the
  // old language until the staleTime expired. Safe ordering is guaranteed by
  // SwitchButton, which persists the preference BEFORE i18n.changeLanguage
  // flips this key and triggers the refetch.
  const { i18n } = useTranslation();
  const { data, error, isLoading } = useQuery({
    queryKey: ['commonListData', optionParams, i18n.language],
    queryFn: () => fetchCommonCodes(optionParams),
    staleTime: 1000 * 60 * 1,
    enabled,
  });

  if (error) {
    console.error('Error fetching common codes:', error);
  }

  return { commonListData: data, error, isLoading };
};

export default useCommonCodes;
